import { Download } from '@drift-labs/icons';
import confetti from 'canvas-confetti';
import Tooltip from 'src/components/Tooltip/Tooltip';
import NumberDisplay from 'src/components/Utils/NumberDisplay';
import useAccountData from 'src/hooks/useAccountData';
import useIsMobileDevice from 'src/hooks/useIsMobileDevice';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useMounted from 'src/hooks/useMounted';
import { useEffect, useRef, useState } from 'react';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import NumLib from 'src/utils/NumLib';
import SwitchInput from '../Inputs/SwitchInput';
import Modal from './Modal';
import MarketIcon from '../Utils/MarketIcon';
import { BigNum, PRICE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import { COMMON_UI_UTILS, MarketId } from '@drift/common';
import useMarketStateStore from 'src/stores/useMarketStateStore';

interface PnlModalProps {
	onClose: () => void;
	modalPosition: DriftStore['pnlModalPosition'];
}

const PnlModal = ({ onClose, modalPosition }: PnlModalProps) => {
	// # Constants
	const currentAccountKey = useDriftAccountStore((s) => s.currentUserKey);
	const setAccountStore = useDriftAccountStore((s) => s.set);
	const setDriftStore = useDriftStore((s) => s.set);

	const { leverageBeforeClose, collateralBeforeClose } =
		useAccountData().pnlInfo;
	const bannerRef = useRef();
	const userAccountData = useAccountData();
	const mounted = useMounted();
	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const isMobileSize = useIsMobileScreenSize();
	const isMobileDevice = useIsMobileDevice();

	// dont allow % pnl to show for lp position
	const isLpPosition = !modalPosition.position.lpShares.eq(ZERO);

	// # State
	const [isPercentagePnl, setIsPercentagePnl] = useState(!isLpPosition);

	const position = modalPosition.position;
	const { baseSize, marketSymbol, entryPrice } = position;
	const exitPrice = modalPosition.actualExitPrice;

	const marketStateStore = useMarketStateStore();
	const markPrice = marketStateStore
		.getMarkPriceForMarket(MarketId.createPerpMarket(position.marketIndex))
		?.toNum();

	const pnl = modalPosition.pnl;

	const direction = baseSize.isNeg() ? 'short' : 'long';

	let leverage: number;
	if (leverageBeforeClose && collateralBeforeClose) {
		leverage = leverageBeforeClose;
	} else {
		leverage = userAccountData.marginInfo.leverage;
	}

	const leverageString = `${leverage.toFixed(2)}x`;

	const pnlPercentageNum = COMMON_UI_UTILS.calculatePnlPctFromPosition(
		position.pnlVsMark,
		position.quoteEntryAmount
	);

	const isPositivePnl = isPercentagePnl ? pnlPercentageNum >= 0 : pnl >= 0;

	const dollarPnl = `${NumLib.formatNum.toNotionalDisplay(pnl)}`;
	const pnlPercentage = (
		savedSettings.includeLeverageInPnl
			? pnlPercentageNum * leverage
			: pnlPercentageNum
	).toFixed(2);

	const pnlSign = isPositivePnl ? '+' : '';

	const { exportComponentAsPNG } = mounted
		? require('react-component-export-image-drift')
		: { exportComponentAsPNG: () => {} };

	const entryExit = [
		{
			label: 'Entry Price',
			value: BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum(),
		},
		{
			label: modalPosition.isClosed ? 'Exit Price' : 'Mark Price',
			value: modalPosition.isClosed ? exitPrice : markPrice,
		},
	];

	// # State-Dependent constants
	const pnlDisplay = isPercentagePnl
		? `${pnlSign}${pnlPercentage}%`
		: dollarPnl;

	// # Methods
	const handleDownload = () => {
		exportComponentAsPNG(bannerRef, {
			fileName: 'pnl',
			html2CanvasOptions: {
				backgroundColor: '#030911',
			},
		});
	};

	const handleClose = () => {
		onClose();

		setAccountStore((state) => {
			state.accounts[currentAccountKey].pnlInfo.leverageBeforeClose = null;
			state.accounts[currentAccountKey].pnlInfo.collateralBeforeClose = null;
		});

		setDriftStore((state) => {
			state.pnlModalPosition = null;
		});
	};

	// # Effect Hooks
	useEffect(() => {
		if (isPositivePnl) {
			confetti({
				colors: [
					'#e8a2a0',
					'#c187c5',
					'#b47ed2',
					'#aa77db',
					'#a070e5',
					'#9468f1',
					'#8d7cef',
					'#8690ee',
					'#7fa4ec',
					'#78b8eb',
					'#71cce9',
				],
				origin: {
					y: 0.7,
				},
				particleCount: 100,
				spread: 75,
			});
		}
	}, []);

	// # Rendering
	return (
		<Modal
			className={isMobileSize ? 'overflow-y-scroll' : ''}
			onClose={handleClose}
			sizeConfig={{
				xs: 12,
				sm: 8,
				md: 6,
			}}
		>
			<Modal.Body className="xs:h-auto xs:w-full">
				<Modal.Header showX={true} onClose={handleClose}>
					<div className="flex items-center justify-between w-full mr-8">
						<div className="text-3xl font-bold text-text-emphasis">
							Share your P&amp;L
						</div>
						{/* TODO - figure out how to download to mobile device */}
						{!isMobileDevice && (
							<div className="flex items-center gap-6 text-xs text-text-label">
								<div className="flex items-center justify-center gap-2">
									<div className="transition-all border rounded-full cursor-pointer border-container-border hover:text-text-emphasis">
										<Download
											className="p-2"
											size={40}
											onClick={handleDownload}
											color={'currentColor'}
										/>
									</div>
									<span className="xs:hidden sm:block">Download</span>
								</div>
							</div>
						)}
					</div>
				</Modal.Header>

				<Modal.Content>
					<>
						<div
							ref={bannerRef}
							className="relative z-10 w-full p-2 pb-8 overflow-hidden rounded-lg bg-container-bg"
						>
							<img
								className="absolute flex items-center -z-10 h-[320px] opacity-40 right-[20px] bottom-[50px]"
								src="/assets/icons/logo-basic.svg"
							/>
							<div className="flex items-center gap-2 mb-8">
								<img src="/assets/icons/logo.svg" height={64} width={64} />
								<div className="h-full">
									<div className="text-2xl font-bold">Drift</div>
									<div className="text-lg text-text-label font-accent">
										app.drift.trade
									</div>
								</div>
							</div>
							<div>
								<span
									className={`mr-2 sm:text-7xl font-semibold xs:text-6xl ${
										isPositivePnl ? 'text-positive-green' : 'text-negative-red'
									}`}
								>
									{pnlDisplay}
								</span>
								<span className="text-lg font-accent">P&amp;L</span>
							</div>
							<div className="z-10 grid items-center w-full grid-rows-2 font-bold sm:grid-cols-2 xs:grid-cols-1 gap-x-6 gap-y-4">
								<div className="flex items-center justify-between h-full gap-2 text-lg">
									<div className="flex items-center w-1/2 h-full gap-2 p-3 rounded-lg bg-input-bg">
										{`${direction[0].toUpperCase()}${direction.slice(1)}`}
										<img
											src={
												direction === 'long'
													? '/assets/icons/line-up.png'
													: '/assets/icons/line-down.png'
											}
											className="w-6 h-4"
										/>
									</div>
									<div className="flex items-center w-1/2 h-full p-3 rounded-lg xs:text-md sm:text-sm xs:justify-start sm:justify-center bg-input-bg">
										{`${leverageString} Leverage`}
									</div>
								</div>
								<div className="flex items-center h-full gap-2 p-3 text-lg align-middle rounded-lg bg-input-bg">
									<MarketIcon marketSymbol={marketSymbol} sizeClass="w-6 h-6" />
									{marketSymbol}
								</div>
								{entryExit.map(({ label, value }) => (
									<div
										key={label}
										className="flex items-center justify-between p-3 rounded-lg bg-input-bg"
									>
										<span className="flex items-center gap-2 text-text-emphasis">
											{label}
										</span>
										<NumberDisplay value={value} displayType="tradePrecision" />
									</div>
								))}
							</div>
						</div>
						<div className="flex justify-end gap-4 px-2">
							<SwitchInput
								checked={savedSettings.includeLeverageInPnl}
								label="Include leverage"
								onChange={() => {
									setSavedSettings({
										includeLeverageInPnl: !savedSettings.includeLeverageInPnl,
									});
								}}
								className="text-xs"
							/>
							<SwitchInput
								checked={!isPercentagePnl}
								disabled={isLpPosition}
								label="Toggle P&amp;L Type"
								onChange={() => {
									setIsPercentagePnl(!isPercentagePnl);
								}}
								className="text-xs"
							/>
							<Tooltip content="Show/hide automatically when closing positions">
								<SwitchInput
									label="Don't show again"
									checked={!savedSettings.showPnlOnClose}
									onChange={() =>
										setSavedSettings({
											...savedSettings,
											showPnlOnClose: !savedSettings.showPnlOnClose,
										})
									}
								/>
							</Tooltip>
						</div>
					</>
				</Modal.Content>
			</Modal.Body>
		</Modal>
	);
};

export default PnlModal;
